import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, query, getDocs, doc, updateDoc, addDoc, serverTimestamp, orderBy } from "firebase/firestore";
import { Search, Loader2, X, Check, FileText } from "lucide-react";

export default function AgentLandingEvaluations() {
    const [evaluations, setEvaluations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEval, setSelectedEval] = useState(null);
    const [statusFilter, setStatusFilter] = useState("pending_review");

    const [reviewData, setReviewData] = useState({
        verifiedHSCode: "",
        brokerNotes: "",
        status: ""
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchEvaluations();
    }, []);

    const fetchEvaluations = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, "landingEvaluations"),
                orderBy("createdAt", "desc")
            );
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEvaluations(data);
        } catch (err) {
            console.error("Error fetching agent evaluations:", err);
        }
        setLoading(false);
    };

    const filtered = evaluations.filter(e => e.status === statusFilter);

    const handleReviewClick = (ev) => {
        setSelectedEval(ev);
        setReviewData({
            verifiedHSCode: ev.verifiedHSCode || ev.hsCode || "",
            brokerNotes: ev.brokerNotes || "",
            status: ev.status || "pending_review"
        });
    };

    const handleSaveReview = async () => {
        if (!selectedEval) return;
        setSaving(true);
        try {
            // 1. Update Evaluation
            const ref = doc(db, "landingEvaluations", selectedEval.id);
            await updateDoc(ref, {
                verifiedHSCode: reviewData.verifiedHSCode,
                brokerNotes: reviewData.brokerNotes,
                status: reviewData.status,
                reviewedAt: serverTimestamp()
            });

            // 2. Notify Client
            if (reviewData.status !== "pending_review") {
                await addDoc(collection(db, "Clients", selectedEval.companyId, "notifications"), {
                    title: "Evaluation Reviewed",
                    message: `Your evaluation for ${selectedEval.productName} has been updated to ${reviewData.status.replace("_", " ")}.`,
                    link: "/client/landing-evaluations",
                    read: false,
                    createdAt: serverTimestamp()
                });
            }

            setEvaluations(prev =>
                prev.map(p => p.id === selectedEval.id ? { ...p, ...reviewData } : p)
            );
            setSelectedEval(null);
        } catch (err) {
            console.error("Error saving review:", err);
            alert("Error saving evaluation review.");
        }
        setSaving(false);
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            <div className="flex items-center justify-between bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <div>
                    <h1 className="text-2xl font-bold text-navy-900 font-serif">Review Landing Evaluations</h1>
                    <p className="text-slate-500 mt-1">Review client HS classifications and landed cost estimates.</p>
                </div>
            </div>

            <div className="flex gap-4">
                {["pending_review", "needs_documents", "approved", "rejected"].map((stat) => (
                    <button
                        key={stat}
                        onClick={() => setStatusFilter(stat)}
                        className={`px-4 py-2 rounded-full text-sm font-medium capitalize border ${statusFilter === stat
                                ? "bg-navy-900 text-white border-navy-900 shadow-sm"
                                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                            }`}
                    >
                        {stat.replace("_", " ")}
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden min-h-[400px]">
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <Loader2 className="animate-spin text-slate-400" size={32} />
                    </div>
                ) : (
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-medium">
                            <tr>
                                <th className="px-6 py-3">Client / Company</th>
                                <th className="px-6 py-3">Product Name</th>
                                <th className="px-6 py-3">Auto HS Code</th>
                                <th className="px-6 py-3">Est. Value</th>
                                <th className="px-6 py-3">Date</th>
                                <th className="px-6 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-10 text-slate-500">
                                        No evaluations in this status.
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-50">
                                        <td className="px-6 py-4 font-medium text-navy-900">{item.companyId}</td>
                                        <td className="px-6 py-4">
                                            {item.productName}
                                            <p className="text-xs text-slate-400 truncate w-48">{item.productDescription}</p>
                                        </td>
                                        <td className="px-6 py-4 font-mono text-slate-700">{item.hsCode}</td>
                                        <td className="px-6 py-4">${(item.estimatedValue || 0).toLocaleString()}</td>
                                        <td className="px-6 py-4 text-slate-500 text-xs text-nowrap">
                                            {item.createdAt ? new Date(item.createdAt.toDate()).toLocaleDateString() : ""}
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => handleReviewClick(item)}
                                                className="text-gold-600 font-bold text-xs uppercase hover:text-gold-500 transition-colors"
                                            >
                                                Review
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {selectedEval && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                            <h2 className="text-lg font-bold text-navy-900 font-serif">Review Evaluation</h2>
                            <button onClick={() => setSelectedEval(null)} className="text-slate-400 hover:text-navy-900">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1 space-y-6">
                            <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg border border-slate-200">
                                <div>
                                    <span className="block text-xs text-slate-500">Product Name</span>
                                    <span className="font-medium text-navy-900">{selectedEval.productName}</span>
                                </div>
                                <div>
                                    <span className="block text-xs text-slate-500">Company ID</span>
                                    <span className="font-medium text-navy-900">{selectedEval.companyId}</span>
                                </div>
                                <div className="col-span-2">
                                    <span className="block text-xs text-slate-500">Description</span>
                                    <span className="text-sm text-navy-900">{selectedEval.productDescription}</span>
                                </div>
                                <div>
                                    <span className="block text-xs text-slate-500">Est. Value (CIF)</span>
                                    <span className="font-bold text-navy-900">${selectedEval.estimatedValue}</span>
                                </div>
                                <div>
                                    <span className="block text-xs text-slate-500">System Classified HS</span>
                                    <span className="font-mono text-slate-700">{selectedEval.hsCode} ({selectedEval.dutyRate}%)</span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Update Status</label>
                                <select
                                    value={reviewData.status}
                                    onChange={(e) => setReviewData({ ...reviewData, status: e.target.value })}
                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-gold-500"
                                >
                                    <option value="pending_review">Pending Review</option>
                                    <option value="needs_documents">Needs Documents</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Verified HS Code</label>
                                <input
                                    type="text"
                                    value={reviewData.verifiedHSCode}
                                    onChange={(e) => setReviewData({ ...reviewData, verifiedHSCode: e.target.value })}
                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-gold-500"
                                    placeholder="e.g. 9503.00.00"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Broker Notes (visible to client)</label>
                                <textarea
                                    rows={3}
                                    value={reviewData.brokerNotes}
                                    onChange={(e) => setReviewData({ ...reviewData, brokerNotes: e.target.value })}
                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded text-sm focus:ring-2 focus:ring-gold-500"
                                    placeholder="Explain classification logic or request documents..."
                                />
                            </div>
                        </div>

                        <div className="p-5 border-t border-slate-100 flex justify-end gap-3 bg-slate-50">
                            <button
                                onClick={() => setSelectedEval(null)}
                                className="px-5 py-2 text-sm font-bold text-slate-600 hover:text-navy-900"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveReview}
                                disabled={saving}
                                className="px-5 py-2 bg-navy-900 text-white rounded text-sm font-bold hover:bg-navy-800 disabled:opacity-50 flex items-center gap-2"
                            >
                                {saving ? (
                                    <>Saving... <Loader2 className="animate-spin" size={16} /></>
                                ) : (
                                    <>Save Review <Check size={16} /></>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
